import React, { useState, useEffect } from 'react'
import {
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  FunnelIcon,
  EyeIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline'
import { useToast } from '@/hooks/useToast'
import axios from 'axios'

interface IntegrationLog {
  id: number
  integration_type: string
  integration_id: number
  operation: string
  status: string
  records_processed: number
  records_success: number
  records_error: number
  error_message?: string
  started_at?: string
  finished_at?: string
  duration_seconds?: number
  created_at: string
}

interface DashboardData {
  period: {
    days: number
    start_date: string
    end_date: string
  }
  summary: {
    total_operations: number
    total_processed: number
    total_success: number
    total_errors: number
    success_rate: number
  }
  status_breakdown: {
    success: number
    error: number
    warning: number
  }
  integration_breakdown: Record<string, number>
  active_connections: Record<string, number>
  recent_operations: IntegrationLog[]
  generated_at: string
}

const ImportsDashboardPage: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [logs, setLogs] = useState<IntegrationLog[]>([])
  const [loading, setLoading] = useState(true)
  const [logsLoading, setLogsLoading] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState(30)
  const [selectedFilter, setSelectedFilter] = useState<string>('')
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const { success, error } = useToast()

  const loadDashboard = async (days: number = 30) => {
    try {
      setLoading(true)
      const token = localStorage.getItem('access_token')

      const response = await axios.get<DashboardData>(
        `http://localhost:8003/api/v1/integrations/history/dashboard?days=${days}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      setDashboardData(response.data)
    } catch (err: any) {
      error('Erro ao carregar dashboard', err.response?.data?.detail || err.message)
    } finally {
      setLoading(false)
    }
  }

  const loadLogs = async (integationType: string = '', status: string = '', limit: number = 20) => {
    try {
      setLogsLoading(true)
      const token = localStorage.getItem('access_token')

      const params = new URLSearchParams()
      if (integationType) params.append('integration_type', integationType)
      if (status) params.append('status', status)
      params.append('limit', limit.toString())

      const response = await axios.get(
        `http://localhost:8003/api/v1/integrations/history/logs?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      setLogs(response.data.logs || [])
    } catch (err: any) {
      error('Erro ao carregar logs', err.response?.data?.detail || err.message)
    } finally {
      setLogsLoading(false)
    }
  }

  useEffect(() => {
    loadDashboard(selectedPeriod)
    loadLogs(selectedFilter, selectedStatus)
  }, [selectedPeriod])

  useEffect(() => {
    loadLogs(selectedFilter, selectedStatus)
  }, [selectedFilter, selectedStatus])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />
      case 'error':
        return <XCircleIcon className="h-5 w-5 text-red-500" />
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-50 text-green-700 border-green-200'
      case 'error':
        return 'bg-red-50 text-red-700 border-red-200'
      case 'warning':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200'
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200'
    }
  }

  const formatIntegrationType = (type: string) => {
    const typeMap: Record<string, string> = {
      'ofx': 'Importação OFX',
      'pluggy': 'Pluggy Open Banking',
      'bank': 'Integração Bancária',
      'payment_gateway': 'Gateway de Pagamento',
      'crypto': 'Exchange Crypto',
      'erp': 'Sistema ERP',
      'accounting': 'Sistema Contábil'
    }
    return typeMap[type] || type.toUpperCase()
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString('pt-BR')
    } catch {
      return dateStr
    }
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A'
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="flex items-center space-x-3">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-600" />
          <span className="text-lg text-gray-600">Carregando dashboard...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ChartBarIcon className="h-8 w-8 text-blue-600" />
          Dashboard de Importações
        </h1>
        <p className="text-gray-600 mt-2">
          Monitoramento e histórico de integrações e importações de dados
        </p>
      </div>

      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">
            Período:
          </label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={7}>Últimos 7 dias</option>
            <option value={30}>Últimos 30 dias</option>
            <option value={90}>Últimos 90 dias</option>
          </select>
        </div>

        <button
          onClick={() => {
            loadDashboard(selectedPeriod)
            loadLogs(selectedFilter, selectedStatus)
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <ArrowPathIcon className="h-4 w-4" />
          <span>Atualizar</span>
        </button>
      </div>

      {dashboardData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DocumentArrowUpIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total de Operações</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardData.summary.total_operations}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Registros Processados</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardData.summary.total_processed.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <ChartBarIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Taxa de Sucesso</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardData.summary.success_rate.toFixed(1)}%</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total de Erros</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardData.summary.total_errors}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Status Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Operações por Status</h3>
              <div className="space-y-4">
                {Object.entries(dashboardData.status_breakdown).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(status)}
                      <span className="capitalize">{status === 'success' ? 'Sucesso' : status === 'error' ? 'Erro' : 'Aviso'}</span>
                    </div>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Operações por Tipo</h3>
              <div className="space-y-4">
                {Object.entries(dashboardData.integration_breakdown).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span>{formatIntegrationType(type)}</span>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
                {Object.keys(dashboardData.integration_breakdown).length === 0 && (
                  <p className="text-gray-500 text-center py-4">Nenhuma operação no período</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Operations */}
          {dashboardData.recent_operations.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Operações Recentes</h3>
              <div className="space-y-3">
                {dashboardData.recent_operations.map((operation) => (
                  <div key={operation.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(operation.status)}
                      <div>
                        <p className="font-medium">{formatIntegrationType(operation.integration_type)}</p>
                        <p className="text-sm text-gray-500">{operation.operation}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{operation.records_processed} registros</p>
                      <p className="text-xs text-gray-500">{formatDate(operation.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Detailed Logs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Histórico Detalhado</h3>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <FunnelIcon className="h-4 w-4 text-gray-400" />
                <select
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Todos os tipos</option>
                  <option value="ofx">Importação OFX</option>
                  <option value="pluggy">Pluggy Open Banking</option>
                  <option value="bank">Integração Bancária</option>
                  <option value="payment_gateway">Gateway de Pagamento</option>
                </select>
              </div>

              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos os status</option>
                <option value="success">Sucesso</option>
                <option value="error">Erro</option>
                <option value="warning">Aviso</option>
              </select>
            </div>
          </div>
        </div>

        <div className="p-6">
          {logsLoading ? (
            <div className="flex items-center justify-center py-8">
              <ArrowPathIcon className="h-6 w-6 animate-spin text-blue-600 mr-2" />
              <span>Carregando logs...</span>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <DocumentArrowUpIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Nenhum log encontrado para os filtros selecionados</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <div key={log.id} className={`p-4 rounded-lg border ${getStatusColor(log.status)}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      {getStatusIcon(log.status)}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{formatIntegrationType(log.integration_type)}</span>
                          <span className="text-sm text-gray-500">•</span>
                          <span className="text-sm text-gray-600">{log.operation}</span>
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          <span>{log.records_processed} processados</span>
                          {log.records_success > 0 && <span className="ml-2">• {log.records_success} sucessos</span>}
                          {log.records_error > 0 && <span className="ml-2 text-red-600">• {log.records_error} erros</span>}
                        </div>
                        {log.error_message && (
                          <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                            {log.error_message}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="text-right text-sm text-gray-500">
                      <div>{formatDate(log.created_at)}</div>
                      {log.duration_seconds && (
                        <div className="mt-1">Duração: {formatDuration(log.duration_seconds)}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ImportsDashboardPage